import { useState, type FormEvent } from 'react';

const FORM_ID = 'dbb1bo6ctmbk4sp5c9t0';
const SUBMIT_ADDR = 'https://readdy.ai/api/form/dbb1bo6ctmbk4sp5c9t0';

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function FeedbackForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('contact_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setMessage('反馈已提交，感谢你的意见。');
      form.reset();
      return;
    }

    const name = String(formData.get('name') ?? '').trim();
    const email = String(formData.get('email') ?? '').trim();
    const topic = String(formData.get('topic') ?? '').trim();
    const content = String(formData.get('message') ?? '').trim();

    if (!name || !email || !content) {
      setStatus('error');
      setMessage('请填写称呼、邮箱与反馈内容后再提交。');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus('error');
      setMessage('邮箱格式看起来不太对，请检查后重新提交。');
      return;
    }

    if (content.length > 500) {
      setStatus('error');
      setMessage('反馈内容不能超过 500 个字符，请适当精简。');
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'contact_alt') return;
      payload.append(key, String(value));
    });
    if (topic) payload.set('topic', topic);

    setStatus('submitting');
    setMessage('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();

      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = serverMessage.toLowerCase().includes('spam');
      const isOk = response.ok && parsed?.code === 'OK';

      if (!isOk || isSpam) {
        setStatus('error');
        setMessage(serverMessage || '提交没有成功，请稍后再试。');
        return;
      }

      setStatus('success');
      setMessage('反馈已提交，感谢你的意见。我们会尽快查看。');
      form.reset();
    } catch {
      setStatus('error');
      setMessage('网络连接出现问题，提交没有成功，请稍后重试。');
    }
  };

  return (
    <form
      id={FORM_ID}
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-300/70 bg-background-100 p-5 md:p-6"
      noValidate
    >
      <div className="mb-5 flex items-center justify-between gap-3">
        <div>
          <p className="eyebrow mb-1.5">Feedback</p>
          <h3 className="text-base font-medium text-foreground-950">反馈与联系</h3>
        </div>
        <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-200 text-primary-400">
          <i className="ri-mail-send-line" />
        </span>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="min-w-0">
          <label htmlFor="feedback-name" className="mb-1.5 block text-[12px] text-foreground-600">
            称呼 <span className="text-primary-400">*</span>
          </label>
          <input
            id="feedback-name"
            name="name"
            type="text"
            required
            maxLength={40}
            placeholder="如何称呼你"
            className="box-border w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-[12px] placeholder:text-foreground-500 focus:border-primary-500/70 focus:ring-1 focus:ring-primary-500/30"
          />
        </div>
        <div className="min-w-0">
          <label htmlFor="feedback-email" className="mb-1.5 block text-[12px] text-foreground-600">
            邮箱 <span className="text-primary-400">*</span>
          </label>
          <input
            id="feedback-email"
            name="email"
            type="email"
            required
            placeholder="用于回复的邮箱地址"
            className="box-border w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-[12px] placeholder:text-foreground-500 focus:border-primary-500/70 focus:ring-1 focus:ring-primary-500/30"
          />
        </div>
      </div>

      <div className="mt-4 min-w-0">
        <label htmlFor="feedback-topic" className="mb-1.5 block text-[12px] text-foreground-600">
          反馈类型
        </label>
        <select
          id="feedback-topic"
          name="topic"
          defaultValue="内容纠错"
          className="box-border w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-500/70 focus:ring-1 focus:ring-primary-500/30"
        >
          <option value="内容纠错">内容纠错</option>
          <option value="条目补充">条目补充</option>
          <option value="版权与删除">版权与删除</option>
          <option value="合作与建议">合作与建议</option>
        </select>
      </div>

      <div className="mt-4 min-w-0">
        <label htmlFor="feedback-message" className="mb-1.5 block text-[12px] text-foreground-600">
          反馈内容 <span className="text-primary-400">*</span>
        </label>
        <textarea
          id="feedback-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="请简要描述你遇到的问题或建议，最多 500 字"
          className="box-border w-full resize-y rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-6 text-foreground-900 outline-none transition-colors placeholder:text-[12px] placeholder:text-foreground-500 focus:border-primary-500/70 focus:ring-1 focus:ring-primary-500/30"
        />
        <p className="mt-1 text-right font-label text-[10px] text-foreground-500">最多 500 字</p>
      </div>

      <div className="form-honeypot-field" aria-hidden="true">
        <label htmlFor="feedback-contact-alt">备用联系方式</label>
        <input
          id="feedback-contact-alt"
          name="contact_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
        />
      </div>

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-[11px] leading-5 text-foreground-500">
          表单仅用于收集反馈意见，不会用于其他用途。
        </p>
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-[13px] font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <i className={status === 'submitting' ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'} />
          {status === 'submitting' ? '提交中…' : '提交反馈'}
        </button>
      </div>

      {message && (
        <p
          role="status"
          className={`mt-4 rounded-md border px-3 py-2.5 text-[12px] leading-6 ${
            status === 'success'
              ? 'border-primary-500/40 bg-primary-500/10 text-primary-300'
              : 'border-accent-400/50 bg-accent-500/10 text-accent-300'
          }`}
        >
          {message}
        </p>
      )}
    </form>
  );
}